import React from 'react';
import { Link } from '@inertiajs/react';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { LegalPage, Section, P, UL } from '~/marketing/LegalPage';
import { legal } from '~/marketing/legalData';
import { routes } from '~/marketing/routes';

/* Adapted from `landing/terms.html`, which was written before the pivot. Four
   things in it described a product this is not, and each is corrected here
   rather than carried across:

   **Monthly only.** It said paid plans are "billed in advance on a monthly
   recurring basis". Annual exists in the catalog and is two months free, so a
   terms page that knows only about monthly leaves the larger commitment
   ungoverned.

   **The price lock was written as conditional on an offer.** "Where we have
   expressly offered a rate locked for the life of your subscription" describes
   a founding-rate promotion, which was dropped on 2026-09-02 and does not
   exist. The real policy is stronger and quieter: `signup_price_cents` records
   what you were shown the day your account was created (the day a trial began,
   until buying stopped requiring one on 2026-10-27) and checkout charges that, with
   nobody told they have a protected price. Stated here as the term it actually
   is.

   **Plan limits named the wrong units.** "Number of brands, tracked prompts,
   and refresh frequency" — refresh frequency does not vary by plan (cadence is
   uniform weekly across all of them, deliberately, because an uneven schedule
   makes every cross-engine view mix vintages) and the prompt count is not a
   plan lever either. What is metered is businesses and queries.

   **It promised an export that does not exist.** "You may export your data at
   any time while your account is active" — there is no export. Deletion is
   what is built, so deletion is what is described. */

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      path={routes.terms}
      description="The terms for using Spruce: what the service does, how subscriptions and trials work, who owns what, and who you are contracting with."
      lede={`These Terms govern your access to and use of ${legal.product} (the "Service"), operated by ${legal.business}. By creating an account or using the Service you agree to them. If you do not agree, do not use the Service.`}
    >
      <Section title="1. The Service">
        <P>
          {legal.product} measures how AI assistants describe and recommend businesses. The Service
          sends a set of questions to third-party AI systems on a schedule, records the responses,
          and presents analysis of those responses to you — including whether your business is
          named, which other businesses are named, and which sources those systems cite.
        </P>
        <P>
          The Service reports on the output of third-party systems we do not control. We do not
          guarantee any particular visibility, ranking, mention or business outcome, and nothing in
          the Service is a promise that any AI assistant will recommend your business.
        </P>
      </Section>

      <Section title="2. Accounts">
        <P>
          You must provide accurate information when creating an account and keep it current. You
          are responsible for safeguarding your credentials and for activity under your account.
          Tell us at {legal.supportEmail} if you believe your account has been compromised.
        </P>
        <P>
          You must be at least 18 years old and, if you use the Service on behalf of an
          organization, authorized to bind that organization to these Terms.
        </P>
      </Section>

      <Section title="3. Acceptable use">
        <P>You agree not to:</P>
        <UL>
          <li>Use the Service to violate any applicable law or the rights of others.</li>
          <li>Attempt to gain unauthorized access to the Service, other accounts, or our infrastructure.</li>
          <li>Interfere with the Service, including by circumventing usage limits or plan quotas.</li>
          <li>Resell, sublicense or redistribute the Service or its output except as your plan permits.</li>
          <li>Use automated means to extract data from the Service.</li>
          <li>Monitor a business you have no legitimate interest in, or use the Service to harass anyone.</li>
        </UL>
      </Section>

      <Section title="4. Plans, billing and renewal">
        <P>
          A new account can start with a 14-day free trial, which takes no credit card, or buy a
          plan right away. Nothing is charged until you choose a plan. If a trial ends before you
          choose one, collection stops until you do.
        </P>
        <P>
          Paid plans are billed in advance, monthly or annually, at the price shown when you
          purchased. <strong>Subscriptions renew automatically</strong> at the end of each billing
          period until canceled. An annual plan gives twelve months for the price of ten.
        </P>
        <P>
          Payments are processed by {legal.mor}, which acts as the merchant of record and authorized
          reseller for purchases of the Service. Your purchase is therefore also subject to{' '}
          {legal.mor}&rsquo;s buyer terms, and {legal.mor} — not us — appears on your statement and
          issues your invoice. Applicable sales tax, VAT or GST is calculated and collected by{' '}
          {legal.mor}.
        </P>
        <P>
          You may cancel at any time from your Plan page or by emailing {legal.supportEmail}.
          Cancellation stops future renewals and your access continues to the end of the period you
          have already paid for. Refunds are governed by our{' '}
          <Link href={routes.refund}>Refund Policy</Link>.
        </P>
        <P>
          <strong>The price you were shown when you created your account is the price you are charged</strong>,
          and it is held for as long as that subscription remains continuously active. We may change
          our published prices; a change applies to new subscriptions, and to yours only if you
          cancel and start a new one. We will give at least 30 days&rsquo; notice by email before any
          change that would affect an existing subscription, and no change is ever applied
          retroactively to a period you have paid for.
        </P>
      </Section>

      <Section title="5. Plan limits">
        <P>
          Each plan includes a stated number of businesses and a monthly allowance of queries — one
          query being one question asked of one AI assistant once. Collection is metered against
          that allowance.
        </P>
        <P>
          <strong>Reaching your allowance pauses collection for the rest of the period. It never
          produces an additional charge</strong>, and nothing is removed when it happens. The
          allowance resets at the start of your next period, and on any plan below Agency, you may
          upgrade at any time to continue sooner.
        </P>
      </Section>

      <Section title="6. Your data">
        <P>
          You retain ownership of what you provide — your business names, websites, locations and
          the markets you ask us to measure. You grant us a limited license to process it solely to
          operate the Service.
        </P>
        <P>
          The measurements and analysis we generate are made available to you for your own business
          use and, where your plan covers client businesses, for use with those clients. Aggregated
          and de-identified statistics that do not identify you or any customer may be used to
          improve the Service and to publish research.
        </P>
        <P>
          You may remove a business and its history at any time, and you may delete your account,
          which removes your businesses, your questions and every answer collected for them. What
          that keeps and why is described in our <Link href={routes.privacy}>Privacy Policy</Link>.
        </P>
      </Section>

      <Section title="7. Third-party services">
        <P>
          The Service depends on third-party AI platforms, search APIs and infrastructure providers.
          Those providers may change, restrict, price or discontinue their offerings at any time,
          and we may add, change or remove supported AI assistants as a result. Coverage of any
          particular AI assistant is not guaranteed for the life of your subscription.
        </P>
      </Section>

      <Section title="8. Availability">
        <P>
          We aim to keep the Service available and reliable but do not promise uninterrupted access.
          We may suspend it for maintenance and may modify or discontinue features. If we
          discontinue the Service entirely we will give reasonable notice and refund any prepaid
          period you cannot use, pro rata.
        </P>
      </Section>

      <Section title="9. Disclaimer of warranties">
        <P>
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. To the maximum
          extent permitted by law we disclaim all warranties, express or implied, including
          merchantability, fitness for a particular purpose and non-infringement. We do not warrant
          that measurements will be error-free or complete, or that they will match what any
          individual sees in a consumer AI product at a given moment.
        </P>
      </Section>

      <Section title="10. Limitation of liability">
        <P>
          To the maximum extent permitted by law, neither party is liable for indirect, incidental,
          special, consequential or punitive damages, or for lost profits, revenue or data. Our
          total aggregate liability arising out of or relating to the Service is limited to the
          amount you paid in the twelve months immediately preceding the event giving rise to the
          claim.
        </P>
        <P>Nothing in these Terms excludes liability that cannot lawfully be excluded.</P>
      </Section>

      <Section title="11. Indemnity">
        <P>
          You agree to indemnify and hold us harmless from claims arising out of your misuse of the
          Service, your violation of these Terms, or your infringement of a third party&rsquo;s
          rights.
        </P>
      </Section>

      <Section title="12. Termination">
        <P>
          You may stop using the Service and close your account at any time. We may suspend or
          terminate your account if you materially breach these Terms, if required by law, or if
          your use poses a risk to the Service or other customers. Where practical we will give
          notice and an opportunity to put it right first.
        </P>
      </Section>

      <Section title="13. Changes to these Terms">
        <P>
          We may update these Terms. If a change is material we will notify you by email or in the
          Service at least 30 days before it takes effect. Continued use after that date is
          acceptance. If you do not accept a change you may cancel before it takes effect.
        </P>
      </Section>

      <Section title="14. Governing law">
        <P>
          These Terms are governed by the laws of {legal.governingLawState}, without regard to
          conflict-of-law principles. The state and federal courts located in{' '}
          {legal.governingLawVenue} have exclusive jurisdiction, except that either party may seek
          injunctive relief in any court of competent jurisdiction.
        </P>
      </Section>

      <Section title="15. Contact">
        <P>
          Questions about these Terms: <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a>,
          or write to us at the address below.
        </P>
      </Section>
    </LegalPage>
  );
}

Terms.layout = withMarketingLayout;
